import {
  ActionSheetOptions,
  actionSheetController,
  modalController,
  ModalOptions,
} from '@ionic/core';
import {
  InviteDocument,
  ConsumeInviteResponse,
  OrganizationDoc,
} from '@sworkit/api';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firebaseFunctions, fst as firestore } from './firebase';
import { getLoggedInUserId } from './firestore-user';
import { translate } from './translate';
import { deleteInvite } from '../components/sw-app/sw-portal/portal-firestore';

export async function getOrgInvites(orgId: string) {
  const orgInviteDocs = await getDocs(
    query(collection(firestore, `invites`), where('orgId', '==', orgId))
  );
  const invites = orgInviteDocs.docs.map((doc) => doc.data() as InviteDocument);
  return invites;
}

export async function getFirestoreInvite(inviteId: string) {
  if (!firestore) return;
  const inviteDoc = await getDoc(doc(firestore, `invites/${inviteId}`));
  const invite = inviteDoc.data() as InviteDocument;
  console.debug('got invite', invite);
  return invite;
}

export async function firebaseConsumeInvite(inviteId: string) {
  // should only call this method in purchase.ts - response logic already handled in redux;
  const uid = getLoggedInUserId();
  if (!uid || !firestore) {
    const error: ConsumeInviteResponse = {
      error: true,
      message: 'You must be signed in to apply an invite code',
      code: 'NOT_SIGNED_IN',
    };
    return error;
  }
  const consumeInvite = httpsCallable(firebaseFunctions, 'consumeInvite');
  const result = await consumeInvite({ inviteId });
  const res = result.data;
  return res as ConsumeInviteResponse;
}

export async function firebaseResendInviteEmail(inviteId: string) {
  // should only call this method in purchase.ts - response logic already handled in redux;
  if (!firestore) return;
  const resendInviteEmail = httpsCallable(
    firebaseFunctions,
    'resendInviteEmail'
  );
  const result = await resendInviteEmail({ inviteId });
  const res = result.data;
  console.debug('invite email re-sent', res);
}

export async function firebaseResendInvites(inviteIds: string[]) {
  return Promise.all(
    inviteIds.map((inviteId) => firebaseResendInviteEmail(inviteId))
  );
}

export async function firebaseRemoveInvites(inviteIds: string[]) {
  return Promise.all(
    inviteIds.map((inviteId) => deleteInvite(inviteId))
  );
}
async function openAddAdminMember(org, sworkitAdmin) {
  const modal = await modalController.create({
    component: 'add-org-admins-page',
    cssClass: 'sw-large-modal',
    componentProps: {
      org,
      sworkitAdmin
    }
  });
  await modal.present();
}
export async function showInviteOptions(
  org: OrganizationDoc,
  sworkitAdmin: boolean
) {
  return new Promise<boolean>(async (resolve) => {
    const orgId = org.id;
    const actionSheetOptions: ActionSheetOptions = {
      cssClass: 'left-align',
      buttons: [
        {
          icon: 'search-outline',
          text: "Add External Admin",
          handler: () => {
            openAddAdminMember(org, sworkitAdmin)
          },
        },
        {
          icon: 'key',
          text: translate('INVITE_ADMIN'),
          handler: () => {
            showInviteModal(orgId, { role: 'editor' }, sworkitAdmin).then(
              resolve
            );
          },
        },
        {
          icon: 'person',
          text: translate('INVITE_MEMBER', 1),
          handler: () => {
            showInviteModal(orgId, null, sworkitAdmin).then(resolve);
          },
        },
        {
          icon: 'people',
          text: translate('INVITE_MULTIPLE_MEMBERS'),
          handler: () => {
            showImportModal(orgId, sworkitAdmin).then(resolve);
          },
        },
        {
          text: translate('CANCEL'),
          role: 'cancel',
        },
      ],
    };
    if (sworkitAdmin && !org.owner) {
      actionSheetOptions.buttons.unshift({
        icon: 'color-wand',
        text: 'Assign Owner',
        handler: () => {
          showInviteModal(orgId, { role: 'owner' }, sworkitAdmin).then(resolve);
        },
      });
    }
    // @todo: upload file here
    if ((org as any).eligibilityFileGcsBucket) {
      actionSheetOptions.buttons.unshift({
        icon: 'document',
        text: 'Upload Eligibility File',
        handler: () => {
          showEligibilityFileImportModal((org as any).eligibilityFileGcsBucket);

          //this.uploadEligibilityFile (cloud function, takes json in csv form, must have org permissions on back end)
        },
      });
    }
    const actionSheet = await actionSheetController.create(actionSheetOptions);
    return actionSheet.present();
  });
}

async function showInviteModal(
  orgId: string,
  inviteDoc?: Partial<InviteDocument>,
  sworkitAdmin?: boolean
) {
  const options: ModalOptions = {
    component: 'add-invite-page',
    cssClass: 'sw-large-modal',
    componentProps: {
      inviteDoc,
      orgId,
      sworkitAdmin,
    },
  };
  const modal = await modalController.create(options);
  await modal.present();
  const { data } = await modal.onDidDismiss();
  if (!data) return false;
  return true;
}

async function showImportModal(orgId: string, sworkitAdmin: boolean) {
  const options: ModalOptions = {
    component: 'add-invites-page',
    cssClass: 'sw-large-modal',
    componentProps: {
      orgId,
      sworkitAdmin,
    },
  };
  const modal = await modalController.create(options);
  await modal.present();
  const { data } = await modal.onDidDismiss();
  if (!data) return false;
  return true;
}

async function showEligibilityFileImportModal(bucket: string) {
  const options: ModalOptions = {
    component: 'add-eligibility-file-modal',
    cssClass: 'sw-small-modal',
    componentProps: {
      bucket,
    },
  };
  const modal = await modalController.create(options);
  await modal.present();
  const { data } = await modal.onDidDismiss();
  if (!data) return false;
  return true;
}
